import { Avatar } from '@/kit/Avatar';
import { statusToneClass } from '@/core/statusTone';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import { selectClient } from '@/core/selectedClient';
import {
  ExportFormatCode,
  type ClientFilter,
  type ClientGridQuery,
  type ClientGridRow,
} from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn, type DataTableSort } from '@/kit/DataTable';
import { openDialog } from '@/kit/dialog';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { Tooltip } from '@/kit/Tooltip';
import { buttonLinkProps } from '../buttonLink';
import { ClientHeader } from './ClientHeader';
import './ClientList.scss';

/** task-29: why Delete is unavailable against a client that holds accounting data. */
const blockedDeleteHint = 'This client holds trial balances or journals and cannot be deleted. Remove that work first.';

const pageSizeOptions = [10, 25, 50, 100];

interface RowActionsProps {
  element: ClientGridRow;
  showingDeleted: boolean;
  onEdit: (row: ClientGridRow) => void;
  onDelete: (row: ClientGridRow) => void;
  onRestore: (row: ClientGridRow) => void;
  onToggleActive: (row: ClientGridRow) => void;
}

function RowActions({ element, showingDeleted, onEdit, onDelete, onRestore, onToggleActive }: RowActionsProps) {
  const navigate = useNavigate();
  return (
    <div className="d-flex align-items-center grid-actions">
      {/* The row's own Open button and the former kebab's "Open" menu item did the same thing; this is the one
          Open button now, kept unconditional (as the menu item was) so it still opens a deleted client too. */}
      <Button
        title="Open"
        aria-label="Open"
        data-testid="open-client-btn"
        onClick={() => {
          selectClient(element.universalId, element.clientName);
          void navigate('/home/taxreturns/dashboard');
        }}
      >
        <Icon name="launch" outlined />
      </Button>
      {!showingDeleted && (
        <Button title="Edit" onClick={() => onEdit(element)}>
          <Icon name="edit" outlined />
        </Button>
      )}
      {/* task-29: a client holding trial balances or journals cannot be deleted. The span carries the tooltip
          because a disabled button raises no pointer events of its own. */}
      {!showingDeleted && (
        <Tooltip title={element.hasAccountingData ? blockedDeleteHint : ''} placement="above">
          <span>
            <Button
              title={element.hasAccountingData ? blockedDeleteHint : 'Delete'}
              disabled={element.hasAccountingData}
              onClick={() => onDelete(element)}
            >
              <Icon name="delete_outline" outlined />
            </Button>
          </span>
        </Tooltip>
      )}
      {!showingDeleted && (
        <Button
          title={element.isActive ? 'Mark as Inactive' : 'Mark as Active'}
          onClick={() => onToggleActive(element)}
        >
          <Icon name={element.isActive ? 'block' : 'check_circle'} outlined />
        </Button>
      )}
      {showingDeleted && (
        <Button title="Restore" onClick={() => onRestore(element)}>
          <Icon name="restore_from_trash" outlined />
        </Button>
      )}
    </div>
  );
}

/** Angular `ClientListComponent`: Manage > Clients grid. */
export function ClientList() {
  const navigate = useNavigate();
  const [totalRecord, setTotalRecord] = useState(0);
  const [dataSource, setDataSource] = useState<ClientGridRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** ap5-a3: true while the grid is showing deleted clients, which offer Restore instead of Delete. */
  const [showingDeleted, setShowingDeleted] = useState(false);
  // prod-p2: the grid is paged on the server; only one page of rows is ever fetched.
  const [pageIndex, setPageIndexState] = useState(0);
  const [pageSize, setPageSizeState] = useState(25);
  const pageIndexRef = useRef(0);
  const pageSizeRef = useRef(25);
  const mounted = useRef(false);
  const [sort, setSort] = useState<DataTableSort>({ key: 'added', ascending: false });
  const queryRef = useRef<ClientGridQuery>({
    pageNumber: 1,
    pageSize: 25,
    // The newest client is listed first; a column header re-sorts the whole list on the server.
    sortBy: 'added',
    sortOrder: false,
    searchText: '',
    // task-27: the header no longer offers a due-date or accounts-status filter, so these
    // stay pinned at the values the API reads as "no filter" and the grid lists every client.
    statusType: -1,
    dueFilter: null,
    clientType: null,
    recordStatus: 0,
    // the list opens on the Active clients (the header's Status filter shows Active)
    isActive: true,
  });

  const setPage = (nextIndex: number, nextSize: number) => {
    pageIndexRef.current = nextIndex;
    pageSizeRef.current = nextSize;
    setPageIndexState(nextIndex);
    setPageSizeState(nextSize);
  };

  function getList(): void {
    practiceService.getClientGrid(queryRef.current).then(
      (response) => {
        if (!mounted.current) return;
        setDataSource(response.clientList);
        setTotalRecord(response.pagination.totalItemCount);
        setSelectedIds(new Set());

        // prod-p2: deleting the last rows of the last page would otherwise leave an empty grid.
        if (response.clientList.length === 0 && pageIndexRef.current > 0) {
          goToPage(pageIndexRef.current - 1, pageSizeRef.current);
        }
      },
      (error) => notifyError(error),
    );
  }

  /** prod-p2: moves the server-side page; the filters and the sort order are kept. */
  function goToPage(nextIndex: number, nextSize: number): void {
    setPage(nextIndex, nextSize);
    queryRef.current = { ...queryRef.current, pageNumber: nextIndex + 1, pageSize: nextSize };
    getList();
  }

  useEffect(() => {
    mounted.current = true;
    getList();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getList only reads refs and stable setters
  }, []);

  /** Header click: the same column flips the direction, a new column starts ascending; back to page 1. */
  const onSort = (key: string) => {
    const ascending = sort.key === key ? !sort.ascending : true;
    setSort({ key, ascending });
    setPage(0, pageSizeRef.current);
    queryRef.current = { ...queryRef.current, sortBy: key, sortOrder: ascending, pageNumber: 1 };
    getList();
  };

  const onFilter = (filter: ClientFilter) => {
    setPage(0, pageSizeRef.current);
    queryRef.current = { ...queryRef.current, ...filter, pageNumber: 1, pageSize: pageSizeRef.current };
    setShowingDeleted(queryRef.current.recordStatus === 1);
    getList();
  };

  const onExport = (format: ExportFormatCode) => {
    const query = queryRef.current;
    practiceService
      .exportClients({
        format,
        ids: [...selectedIds],
        searchText: query.searchText,
        statusType: query.statusType ?? -1,
        clientType: query.clientType,
        dueFilter: query.dueFilter,
        isActive: query.isActive,
      })
      .catch((error) => notifyError(error));
  };

  const idsFor = (row: ClientGridRow): string[] =>
    selectedIds.has(row.universalId) && selectedIds.size > 1 ? [...selectedIds] : [row.universalId];

  /** Marks the row, or the whole selection when the row is part of it, active or inactive. */
  const onToggleActiveClick = (row: ClientGridRow) => {
    const ids = idsFor(row);
    const isActive = !row.isActive;

    practiceService.setClientStatus(ids, isActive).then(
      () => {
        notifySuccess(isActive ? 'Client marked as active.' : 'Client marked as inactive.');
        getList();
      },
      (error) => notifyError(error),
    );
  };

  const onEditClick = (row: ClientGridRow) => {
    void navigate(matrixPath('/home/manage', 'addClient', { id: btoa(row.universalId) }));
  };

  const isAllSelected = dataSource.length > 0 && dataSource.every((row) => selectedIds.has(row.universalId));

  const toggleAll = (checked: boolean) => {
    setSelectedIds(new Set(checked ? dataSource.map((row) => row.universalId) : []));
  };

  const toggleRow = (row: ClientGridRow, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.universalId);
      } else {
        next.delete(row.universalId);
      }
      return next;
    });
  };

  /**
   * task-29: the API refuses the whole batch when any client holds trial balances or
   * journals, so naming them here saves a round trip that could only ever fail. The API
   * stays the restriction: a row whose flag has not arrived yet is still refused there.
   */
  const blockedBy = (ids: string[]): string[] => {
    const selected = new Set(ids);
    return dataSource
      .filter((row) => selected.has(row.universalId) && row.hasAccountingData)
      .map((row) => row.clientName);
  };

  const confirmDelete = (ids: string[]) => {
    const blocked = blockedBy(ids);
    if (blocked.length > 0) {
      notifyError(
        `${blocked.join(', ')} ${blocked.length > 1 ? 'hold' : 'holds'} accounting data and cannot be deleted. Remove the trial balances and journals first.`,
      );
      return;
    }

    const dialogRef = openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 ? 'Clients' : 'Client', count: ids.length },
    });

    void dialogRef.afterClosed().then((confirmed) => {
      if (!confirmed) {
        return;
      }
      practiceService.deleteClients(ids).then(
        () => {
          notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
          getList();
        },
        // task-29: a client holding accounting data comes back as a 409 whose problem
        // details name it, and notifyError renders those, so nothing is decided here.
        (error) => notifyError(error),
      );
    });
  };

  const restore = (ids: string[]) => {
    practiceService.restoreClients(ids).then(
      () => {
        notifySuccess(ids.length > 1 ? 'Clients restored successfully.' : 'Client restored successfully.');
        getList();
      },
      (error) => notifyError(error),
    );
  };

  const columns: DataTableColumn<ClientGridRow>[] = [
    {
      id: 'id',
      header: (
        <>
          <Checkbox checked={isAllSelected} onChange={(event) => toggleAll(event.target.checked)} />
          <button
            type="button"
            className="table-sort-button"
            aria-label="Sort by ID"
            onClick={() => onSort('clientId')}
          >
            ID
            <span
              className="table-sort-arrow"
              data-dir={sort.key === 'clientId' ? (sort.ascending ? 'asc' : 'desc') : 'none'}
              aria-hidden="true"
            />
          </button>
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={selectedIds.has(element.universalId)}
            onChange={(event) => toggleRow(element, event.target.checked)}
          />
          <a className="link" {...buttonLinkProps(() => onEditClick(element))}>
            {element.clientId}
          </a>
        </>
      ),
    },
    {
      id: 'clientname',
      sortKey: 'clientName',
      header: 'Client Name',
      cell: (element) => (
        <span className="name-cell">
          <Avatar name={element.clientName} />
          {/* Issue #28 (QA): the name opens the client's details, as the ID does. The row's Open button still opens the
              client in Accounts. */}
          <a
            className="link client-name-link text-ellipsis-cell"
            title={`${element.clientName} - edit this client`}
            data-testid="edit-client-link"
            {...buttonLinkProps(() => onEditClick(element))}
          >
            {element.clientName}
          </a>
        </span>
      ),
    },
    { id: 'clienttype', sortKey: 'clientType', header: 'Client Type', cell: (element) => element.clientType },
    { id: 'period', header: 'Accounting Period', cell: (element) => element.period },
    { id: 'date', sortKey: 'duedate', header: 'Due Date', cell: (element) => element.dueDate },
    {
      id: 'clientstatus',
      header: 'Status',
      // APT240: a deleted client still carries its last Active/Inactive flag, which read as "Active" in the deleted
      // list. The row has no deleted flag of its own, but this view only lists deleted clients, so it says so (3.0's
      // red "Deleted" badge).
      cell: (element) =>
        showingDeleted ? (
          <span className="status-chip status-deleted">Deleted</span>
        ) : (
          <span className={`status-chip ${element.isActive ? 'status-active' : 'status-inactive'}`}>
            {element.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
    },
    {
      id: 'status',
      header: 'Accounts Status',
      cell: (element) => (
        <span className={statusToneClass(element.status) || 'text-green'}>{element.status}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <RowActions
          element={element}
          showingDeleted={showingDeleted}
          onEdit={onEditClick}
          onDelete={(row) => confirmDelete(idsFor(row))}
          onRestore={(row) => restore(idsFor(row))}
          onToggleActive={onToggleActiveClick}
        />
      ),
    },
  ];

  return (
    <div className="content client-list manage-client-list">
      <div className="content-body">
        <ClientHeader onFilterChanged={onFilter} onExportRequested={onExport} />

        {/* ap5-a3: selecting rows now offers the actions that apply to a selection. */}
        {selectedIds.size > 0 && (
          <div className="d-flex align-items-center gap-15 mb-1">
            <span>{selectedIds.size} selected</span>
            {!showingDeleted && (
              <Button
                className="action-button danger-button"
                onClick={() => selectedIds.size > 0 && confirmDelete([...selectedIds])}
              >
                <Icon name="delete_outline" outlined />
                <span>Delete selected</span>
              </Button>
            )}
            {showingDeleted && (
              <Button
                className="action-button secondary-button"
                onClick={() => selectedIds.size > 0 && restore([...selectedIds])}
              >
                <Icon name="restore_from_trash" outlined />
                <span>Restore selected</span>
              </Button>
            )}
            <Button className="action-button secondary-button" onClick={() => onExport(ExportFormatCode.Excel)}>
              <Icon name="border_all" className="icon-color-xls" />
              <span>Export selected</span>
            </Button>
          </div>
        )}

        <div className={totalRecord > 0 ? 'mat-table-wrapper mat-table-wrapper-pagination' : 'mat-table-wrapper'}>
          <DataTable
            columns={columns}
            rows={dataSource}
            rowKey={(row) => row.universalId}
            stickyHeader
            sort={sort}
            onSort={onSort}
          />
        </div>

        {dataSource.length === 0 && <div className="text-align-center p-1">No clients match these filters.</div>}

        {/* prod-p2: the grid is paged on the server, so hundreds of clients load one page at a time. */}
        {totalRecord > 0 && (
          <Paginator
            length={totalRecord}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageSizeOptions={pageSizeOptions}
            showFirstLastButtons
            onPage={(event) => goToPage(event.pageIndex, event.pageSize)}
          />
        )}
      </div>
    </div>
  );
}
